import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import NotificationsFilled from '@mui/icons-material/Notifications';
import Stack from '@mui/material/Stack';
import {
  UptimeMarketPlace,
  UptimeOutageNotification,
} from '@uptime/uptime-component';
import { useBlockHistory, useLanguageCode, useUserData } from 'hooks';
import { OutageItem } from 'models/types/Notification';
import useFeatureFlags from 'pages/HomePage/hooks/useFeatureFlags';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import articleActions from 'redux/actions/articleAction';
import { RootState } from 'redux/store';
import { KnowledgeDetail as KnowledgeDetailPath, ManageRequestTicketPath, MarketplaceApprovalsPath, ViewOrderDetailPath } from 'utils/routes';
import { GenerateDataTranslation } from 'utils/translation';
import { StyledBadge } from '../TopNav.style';
import { checkEnabledFeature, getKeyFeaturesConfig } from 'utils/function';
import { ResourceType } from 'components/MenuTicket/types';
import { useMarketplaceProvider } from 'containers/MarketplaceContainer';
import MarketplaceAction from 'redux/actions/marketplaceAction';
import moment from 'moment';
import DateUtils from 'utils/date';
import OrdersNotification from './OrdersNotification';
import { useChatWidget } from 'containers/ChatWidgetContainer';

const Notification = () => {
  const history = useHistory();
  const { t } = useTranslation(['notifications', 'ticketRequest', 'requests', 'home-accessibility']);
  const notificationsTranslation = GenerateDataTranslation("notifications");
  const commonTranslation = GenerateDataTranslation('commonStatus');
  const { handleWidgetIndex } = useChatWidget();
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { currentUser } = useSelector((state: RootState) => state.baseReducer);
  const { permission: { knowledge, requestTypes, delegation }, generalConfig } = useSelector((state: RootState) => state.configReducer);

  const pageSize = generalConfig?.paginationConfig?.limit ?? 10;
  const notificationEnabled = generalConfig?.notifications?.enabled || false;
  const { isMarketplaceEnabled } = useMarketplaceProvider();
  const { user, listRequestsOpen, listApprovalOpen } = useSelector(
    (state: RootState) => state.marketplaceReducer,
  );
  const userData = useUserData();
  const isDelegationEnabled =
  delegation?.enabled && checkEnabledFeature(delegation);
  const getEnabledRequests = getKeyFeaturesConfig(requestTypes?.features, ResourceType.Requests);
  const isCheckEnabledRequests = requestTypes?.enabled && checkEnabledFeature(getEnabledRequests);
  const getEnabledApprovals = getKeyFeaturesConfig(
    requestTypes?.features,
    ResourceType.Approvals,
  );
  const isCheckEnabledApprovals =
    requestTypes?.enabled && checkEnabledFeature(getEnabledApprovals);
  const [listRequestOpen, setListRequestOpen] = useState<any>();
  const [approvalList, setApprovalList] = useState<any>();

  const { announcementList, majorOutageList } = useSelector((state: RootState) => state.articleReducer);
  const { isOpenSubmenu } = useSelector((state: RootState) => state.baseReducer);
  const { isCheckOutageKnowledge } = useFeatureFlags({ knowledge } as any);

  const closeNotification = () => {
    setOpen(false)
  };

  const languageCode = useLanguageCode();

  useBlockHistory(() => {
    closeNotification();
  });

  useEffect(() => {
    if (!currentUser?.user_name || !isCheckOutageKnowledge || currentUser?.lockedOut) return;

    const params = {
      user_name: currentUser?.user_name,
      page_index: 1,
      language: languageCode.snow,
    };
    dispatch(articleActions.getListAnnouncement(params));
  }, [currentUser?.user_name, isCheckOutageKnowledge, languageCode.snow, currentUser?.lockedOut]);

  useEffect(() => {
    if (!userData || !isCheckEnabledRequests || !open || !notificationEnabled) return;

    const params = {
      user_name: userData?.user_name,
      delegation: isDelegationEnabled,
      states: 'open',
      rh_user_id: user?.id || '',
      page_size: pageSize
    }
    dispatch(MarketplaceAction.getAllMarketplaceRequestsOpen(params));
  }, [
      isCheckEnabledRequests,
      userData?.user_name,
      open,
      isMarketplaceEnabled,
      isDelegationEnabled,
      notificationEnabled
    ]);
  useEffect(() => {
    if (!userData || !isCheckEnabledApprovals || !open || !notificationEnabled) return;

    const params = {
      user_sys_id: userData?.uid,
      delegation: isDelegationEnabled,
      states: 'open',
      rh_user_id: user?.id || '',
      page_size: pageSize
    }
    dispatch(MarketplaceAction.getAllMarketplaceApprovalOpen(params));
  }, [
      isCheckEnabledApprovals,
      userData?.uid,
      open,
      isMarketplaceEnabled,
      isDelegationEnabled,
      notificationEnabled
    ]);

  useEffect(() => {
    if(!listRequestsOpen.length){
      setListRequestOpen(null);
    } else {
      let dataReplaceOpen = listRequestsOpen?.map((item: any) => {
        return {
          ...item,
          _id: item?._id || item?.number,
          state: item?.state || "Open",
          originalState: item?.variables ? (item?.variables?.stage?.display_value || item?.stage) : "pending_approval",
          isMarketplaceList: item?.variables ? false : true,
          isRequestItem: true,
          updatedOn: item?.updateDate || moment.utc(item?.approval?.approved_on || item?.createdOn).format('YYYY-MM-DD HH:mm:ss')
        };
      });
      
      const tempOpen = { data: dataReplaceOpen };
      setListRequestOpen(tempOpen);
    }
  }, [listRequestsOpen]);
  useEffect(() => {
    if(!listApprovalOpen.length){
      setApprovalList(null);
    } else {
      let dataReplacePending = listApprovalOpen?.map((item: any) => {
        return {
          ...item,
          _id: item?._id || item?.requestItem?.number,
          isMarketplaceList: item?.requestItem ? false : true,
          isRequestItem: false,
          updatedOn: item?.updatedOn || moment.utc(item?.order_detail?.createdOn || item?.date).format('YYYY-MM-DD HH:mm:ss'),
          isApproval: true,
        };
      });
      const tempPending = { data: dataReplacePending }
      setApprovalList(tempPending);
    }
  }, [listApprovalOpen]);

  const listRequest = useMemo(() => {
    const data: any = listRequestOpen 
    ? DateUtils.sortArrayByDateProperty(listRequestOpen?.data, 'updatedOn', false) 
    : null;
    
    return ({ data });
  }, [listRequestOpen]);
  const listApproval = useMemo(() => {
    const data: any = approvalList 
    ? DateUtils.sortArrayByDateProperty(approvalList?.data, 'updatedOn', false) 
    : null;
    
    return ({ data });
  }, [approvalList]);
  
  const handleViewDetailsClick = (item: OutageItem) => {
    const path = KnowledgeDetailPath.replace(':id', item.number);
    history.push(path);
  };

  const handleOrdersViewDetailsClick = (item: any) => {
    if (item.isMarketplaceList) {
      const path = ViewOrderDetailPath.replace(':id', item._id);
      history.push(path);
    } else if (item.isRequestItem) {
      const path = ManageRequestTicketPath + `?ticketNumber=${item._id}`;
      history.push(path);
    } else {
      const path = MarketplaceApprovalsPath + `?ticketNumber=${item._id}`;
      history.push(path);
    }
  };

  return (
    <>
      <StyledBadge
        variant="dot"
        color="error"
        invisible={majorOutageList?.length === 0 && announcementList?.length === 0}
      >
        <UptimeMarketPlace.IconButton
          aria-label={t('home-accessibility:notificationIcon')}
          icon={NotificationsFilled}
          onClick={() => setOpen(prevState => !prevState)}
        />
      </StyledBadge>

      <UptimeMarketPlace.Drawer isOpenSubmenu={isOpenSubmenu} open={open} onClose={closeNotification} handleWidgetIndex={handleWidgetIndex}>
        <Stack
          p={3}
          borderBottom={1}
          borderColor="divider"
          direction="row"
          alignItems="center"
          gap={2}
          bgcolor="common.white"
        >
          <UptimeMarketPlace.Typography variant="h4" flex="1">
            {t("outagesTitle")}
          </UptimeMarketPlace.Typography>

          <UptimeMarketPlace.Button
            variant="text"
            size="small"
            startIcon={<DoneAllRounded />}
            disabled={majorOutageList?.length === 0 && announcementList?.length === 0}
            sx={{ display: 'none' }}
          >
            {t("clearAllButton")}
          </UptimeMarketPlace.Button>
        </Stack>

        <Stack p={{ xs: '14px', md: 3 }} bgcolor="common.white" borderBottom={1} borderColor="divider">
          <Stack gap="10px">
            {majorOutageList?.map((item: OutageItem) => (
              <UptimeOutageNotification
                key={item.id}
                outage={item}
                onViewDetailsClick={handleViewDetailsClick}
                locale={notificationsTranslation}
                title={notificationsTranslation?.criticalOutage}
              />
            ))}
            {announcementList.map((item: OutageItem) => (
              <UptimeOutageNotification
                key={item.id}
                outage={item}
                onViewDetailsClick={handleViewDetailsClick}
                locale={notificationsTranslation}
              />
            ))}
          </Stack>
          {majorOutageList.length === 0 && announcementList.length === 0 && (
            <UptimeOutageNotification
              title={t("noActiveOutages")}
              alertProps={{
                severity: 'success',
                icon: <CheckCircleRounded fontSize="inherit" />,
                sx: { '.MuiAlertTitle-root': { m: 0 } },
              }}
            />
          )}
        </Stack>
        {
          (notificationEnabled && (listRequest?.data?.length > 0 || listApproval?.data?.length > 0)) && (
            <>
              <Stack
                p={3}
                borderBottom={1}
                borderColor="divider"
                direction="row"
                alignItems="center"
                gap={2}
                bgcolor="common.white"
              >
                <UptimeMarketPlace.Typography variant="h4" flex="1">
                  {t("title")}
                </UptimeMarketPlace.Typography>
              </Stack>

              <Stack p={{ xs: '14px', md: 3 }} bgcolor="common.white">
                <Stack gap="10px">
                  {listRequest?.data?.length > 0 
                    && listRequest?.data?.map((item: any) => {
                      return (
                        <OrdersNotification key={item._id} item={item} onViewDetailsClick={handleOrdersViewDetailsClick} notificationsLocale={notificationsTranslation}
                        delegatedLocale={t('ticketRequest:delegated')}
                        statusLocale={t('requests:tooltipStatusMessage.pendingApproval')}
                        commonStatus={commonTranslation}/>
                      )
                    }
                  )}
                  {listApproval?.data?.length > 0 
                    && listApproval?.data?.map((item: any) => {
                      return (
                        <OrdersNotification key={item._id} item={item} onViewDetailsClick={handleOrdersViewDetailsClick} notificationsLocale={notificationsTranslation}
                        delegatedLocale={t('ticketRequest:delegated')}
                        statusLocale={t('requests:tooltipStatusMessage.pendingApproval')}
                        commonStatus={commonTranslation}/>
                      )
                    }
                  )}
                </Stack>
              </Stack>
            </>
          )
        }
      </UptimeMarketPlace.Drawer>
    </>
  );
};

export default Notification;
